import React, { useState } from "react";
import { TextField, cn, EyeOnIcon, EyeOffIcon  } from "@fmc-digital/ui";
import { resetPasswordFieldsStyles } from "./reset-password-fields.styles";
import type { ResetPasswordFieldsProps } from "./reset-password-fields.types";

/** Must match `getPasswordRequirements` length rule (8–21 characters). */
const RESET_PASSWORD_MAX_LENGTH = 21;

/**
 * ResetPasswordFields Molecule
 * Combines two password input fields with visibility toggles
 * Handles password visibility toggle state
 */
export const ResetPasswordFields: React.FunctionComponent<
  ResetPasswordFieldsProps
> = ({ form, onSubmit, children }) => {
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const toggleNewPasswordVisibility = () => {
    setShowNewPassword((prev) => !prev);
  };

  const toggleConfirmPasswordVisibility = () => {
    setShowConfirmPassword((prev) => !prev);
  };

  const newPasswordInputType: "text" | "password" = showNewPassword
    ? "text"
    : "password";
  const confirmPasswordInputType: "text" | "password" = showConfirmPassword
    ? "text"
    : "password";

  // New password field props
  const newPasswordFieldProps = {
    id: "newPassword",
    name: "newPassword",
    label: "New Password",
    placeholder: "Enter New Password",
    fullWidth: true,
    variant: "outlined" as const,
    type: newPasswordInputType,
    maxLength: RESET_PASSWORD_MAX_LENGTH,
    value: form.watch("newPassword") ?? "",
    onChange: (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      form.setValue("newPassword", e.target.value, {
        shouldValidate: true,
      });
    },
    onBlur: () => form.trigger("newPassword"),
    autoComplete: "new-password" as const,
    error: form.formState.errors.newPassword?.message,
    endIcon: (
      <button
        type="button"
        onClick={(e) => {
          e.preventDefault();
          e.stopPropagation();
          toggleNewPasswordVisibility();
        }}
        className="p-0 m-0 border-0 bg-transparent cursor-pointer hover:opacity-70 focus:outline-none inline-flex items-center justify-center"
        data-testid={showNewPassword ? "new-password-eye" : "new-password-eye-slash"}
        aria-label={showNewPassword ? "Hide password" : "Show password"}
      >
        {showNewPassword ? (
          <EyeOnIcon className="w-6 h-6" />
        ) : (
          <EyeOffIcon className="w-6 h-6" />
        )}
      </button>
    ),
  };

  // Confirm password field props
  const confirmPasswordFieldProps = {
    id: "confirmPassword",
    name: "confirmPassword",
    label: "Confirm New Password",
    placeholder: "Confirm New Password",
    fullWidth: true,
    variant: "outlined" as const,
    type: confirmPasswordInputType,
    maxLength: RESET_PASSWORD_MAX_LENGTH,
    value: form.watch("confirmPassword") ?? "",
    onChange: (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      form.setValue("confirmPassword", e.target.value, {
        shouldValidate: true,
      });
    },
    onBlur: () => form.trigger("confirmPassword"),
    autoComplete: "new-password" as const,
    error: form.formState.errors.confirmPassword?.message,
    endIcon: (
      <button
        type="button"
        onClick={(e) => {
          e.preventDefault();
          e.stopPropagation();
          toggleConfirmPasswordVisibility();
        }}
        className="p-0 m-0 border-0 bg-transparent cursor-pointer hover:opacity-70 focus:outline-none inline-flex items-center justify-center"
        data-testid={showConfirmPassword ? "confirm-password-eye" : "confirm-password-eye-slash"}
        aria-label={showConfirmPassword ? "Hide password" : "Show password"}
      >
        {showConfirmPassword ? (
          <EyeOnIcon className="w-6 h-6" />
        ) : (
          <EyeOffIcon className="w-6 h-6" />
        )}
      </button>
    ),
  };

  return (
    <form
      onSubmit={onSubmit}
      className={cn(resetPasswordFieldsStyles())}
      data-testid="reset-password-fields"
    >
      <div className="form-group">
        <TextField {...newPasswordFieldProps} />
      </div>

      <div className="form-group">
        <TextField {...confirmPasswordFieldProps} />
      </div>

      {children}
    </form>
  );
};
